import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";

import "./ElectrostacticGridTransformerFaceplate.css";
import { TransformerContext } from "../context/TransformerContext";

function ElectrostacticGridTransformerFaceplate({
  tag = "TR-201",
  x = 100,
  y = 100,
  onClose = () => {},
  onOpenGraph = () => {},
}) {
  /* =====================================================
     CONTEXT
  ===================================================== */
  const { transformerData, updateTransformer } =
    useContext(TransformerContext);

  const transformer =
    transformerData?.[tag] ?? {};

  /* =====================================================
     COMMAND VALUES (INPUTS)
  ===================================================== */
  const running =
    transformer.running ?? false;

  const voltage = Number(
    transformer.voltage ?? 22
  );

  const maxTemp = Number(
    transformer.maxTemp ?? 80
  );

  /* =====================================================
     FORCE REFRESH FOR localStorage LIVE VALUES
  ===================================================== */
  const [, setRefresh] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setRefresh((v) => v + 1);
    }, 500);

    return () =>
      clearInterval(timer);
  }, []);

  /* =====================================================
     LIVE VALUES FROM CrudeDehyd.js localStorage
  ===================================================== */
  const flow = Number(
    localStorage.getItem("FIC_18001")
  ) || 0;

  const inletBSW = Number(
    localStorage.getItem("inletBSW")
  ) || 0;

  const finalBSW = Number(
    localStorage.getItem("finalBSW")
  ) || 0;

  const inletSalinity = Number(
    localStorage.getItem("inletSalinity")
  ) || 0;

  const finalSalinity = Number(
    localStorage.getItem("finalSalinity")
  ) || 0;

  const TT_18001 = Number(
    localStorage.getItem("TT_18001")
  ) || 0;

  const TT_18002 = Number(
    localStorage.getItem("TT_18002")
  ) || 22;

  const TT_18003 = Number(
    localStorage.getItem("TT_18003")
  ) || 22;

  const PT_18002 = Number(
    localStorage.getItem("PT_18002")
  ) || 0;

  const efficiency = Number(
    localStorage.getItem("efficiency")
  ) || 0;

  const loadPercent =
    maxTemp > 0
      ? Math.min(
          (TT_18002 / maxTemp) * 100,
          100
        )
      : 0;

  const secondaryCurrent =
    running
      ? 100 +
        (loadPercent / 100) * 80
      : 0;

  /* =====================================================
     LOCAL UI
  ===================================================== */
  const [trip, setTrip] =
    useState(false);

  const [blink, setBlink] =
    useState(false);

  const [position, setPosition] =
    useState({ x, y });

  const [dragging, setDragging] =
    useState(false);

  const dragRef = useRef({
    offsetX: 0,
    offsetY: 0,
  });

  /* =====================================================
     BLINK
  ===================================================== */
  useEffect(() => {
    if (trip) {
      const timer = setInterval(() => {
        setBlink((p) => !p);
      }, 500);

      return () =>
        clearInterval(timer);
    } else {
      setBlink(false);
    }
  }, [trip]);

  /* =====================================================
     DRAG
  ===================================================== */
  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;

      setPosition({
        x:
          e.clientX -
          dragRef.current.offsetX,
        y:
          e.clientY -
          dragRef.current.offsetY,
      });
    };

    const up = () =>
      setDragging(false);

    window.addEventListener(
      "mousemove",
      move
    );

    window.addEventListener(
      "mouseup",
      up
    );

    return () => {
      window.removeEventListener(
        "mousemove",
        move
      );

      window.removeEventListener(
        "mouseup",
        up
      );
    };
  }, [dragging]);

  const startDrag = (e) => {
    setDragging(true);

    dragRef.current.offsetX =
      e.clientX - position.x;

    dragRef.current.offsetY =
      e.clientY - position.y;
  };

  /* =====================================================
     CONTROLS
  ===================================================== */
  const handleStart = () => {
    updateTransformer(tag, {
      running: true,
    });

    setTrip(false);
  };

  const handleStop = () => {
    updateTransformer(tag, {
      running: false,
    });
  };

  const raiseVoltage = () => {
    if (!running) return;

    updateTransformer(tag, {
      voltage: Math.min(
        voltage + 1,
        35
      ),
    });
  };

  const lowerVoltage = () => {
    if (!running) return;

    updateTransformer(tag, {
      voltage: Math.max(
        voltage - 1,
        5
      ),
    });
  };

  const handleMaxTempChange = (e) => {
    updateTransformer(tag, {
      maxTemp:
        Number(e.target.value) || 0,
    });
  };

  const statusText = trip
    ? "TRIP"
    : running
    ? "RUN"
    : "STOP";

  /* =====================================================
     UI
  ===================================================== */
  return (
    <div
      className={`egt-faceplate ${
        dragging
          ? "egt-dragging"
          : ""
      }`}
      style={{
        left: position.x,
        top: position.y,
        width: 360,
      }}
    >
      {/* HEADER */}
      <div
        className="egt-header"
        onMouseDown={startDrag}
      >
        <div className="egt-title">
          {tag}
        </div>

        <div className="egt-header-right">
          <button
            className="egt-btn egt-graph-btn"
            onClick={onOpenGraph}
          >
            📈
          </button>

          <button
            className="egt-btn egt-close-btn"
            onClick={onClose}
          >
            ✕
          </button>

          <div
            className={`egt-status ${
              running
                ? "egt-run"
                : "egt-stop"
            } ${
              blink
                ? "egt-blink"
                : ""
            }`}
          >
            {statusText}
          </div>
        </div>
      </div>

      {/* PROCESS + SALT */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div className="egt-card">
          <div className="egt-section-title">
            PROCESS
          </div>

          <div className="egt-row">
            <span>Flow</span>
            <span>
              {flow.toFixed(1)}
            </span>
          </div>

          <div className="egt-row">
            <span>W In</span>
            <span>
              {inletBSW.toFixed(2)}%
            </span>
          </div>

          <div className="egt-row">
            <span>W Out</span>
            <span>
              {finalBSW.toFixed(2)}%
            </span>
          </div>

          <div className="egt-row">
            <span>Eff</span>
            <span>
              {efficiency.toFixed(1)}%
            </span>
          </div>
        </div>

        <div className="egt-card">
          <div className="egt-section-title">
            SALT
          </div>

          <div className="egt-row">
            <span>In</span>
            <span>
              {inletSalinity.toFixed(0)}
            </span>
          </div>

          <div className="egt-row">
            <span>Out</span>
            <span>
              {finalSalinity.toFixed(0)}
            </span>
          </div>

          <div className="egt-row">
            <span>MPa</span>
            <span>
              {PT_18002.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* TEMP + SETTINGS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div className="egt-card">
          <div className="egt-section-title">
            TEMP
          </div>

          <div className="egt-row">
            <span>In</span>
            <span>
              {TT_18001.toFixed(1)}°C
            </span>
          </div>

          <div className="egt-row">
            <span>TR</span>
            <span>
              {TT_18002.toFixed(1)}°C
            </span>
          </div>

          <div className="egt-row">
            <span>Out</span>
            <span>
              {TT_18003.toFixed(1)}°C
            </span>
          </div>
        </div>

        <div className="egt-card">
          <div className="egt-section-title">
            SETTINGS
          </div>

          <div className="egt-row">
            <span>Max T</span>

            <input
              className="egt-input"
              type="number"
              value={maxTemp}
              onChange={
                handleMaxTempChange
              }
            />
          </div>

          <div className="egt-row">
            <span>kV</span>
            <span>
              {voltage.toFixed(1)}
            </span>
          </div>

          <div className="egt-controls-row">
            <button
              className="egt-btn"
              onClick={raiseVoltage}
            >
              +
            </button>

            <button
              className="egt-btn"
              onClick={lowerVoltage}
            >
              −
            </button>
          </div>
        </div>
      </div>

      {/* ELECTRICAL */}
      <div
        className="egt-card"
        style={{
          marginBottom: 8,
        }}
      >
        <div className="egt-section-title">
          ELECTRICAL
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "1fr 1fr",
            gap: 8,
          }}
        >
          <div className="egt-row">
            <span>Load</span>
            <span>
              {loadPercent.toFixed(
                1
              )}
              %
            </span>
          </div>

          <div className="egt-row">
            <span>mA</span>
            <span>
              {secondaryCurrent.toFixed(
                1
              )}
            </span>
          </div>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="egt-controls">
        <button
          className="egt-btn egt-start"
          onClick={handleStart}
        >
          START
        </button>

        <button
          className="egt-btn egt-stop-btn"
          onClick={handleStop}
        >
          STOP
        </button>

        <button
          className="egt-btn"
          onClick={() =>
            setTrip(false)
          }
        >
          RESET
        </button>
      </div>
    </div>
  );
}

export default ElectrostacticGridTransformerFaceplate;